// CPL deal structures and NJ RIP rules (N.J.A.C. 13:2-24.1).

import type { Cents } from './money.js';
import { addDays, type IsoDate } from './dates.js';

export type QtyUnit = 'case' | 'bottle';

export interface DiscountTier {
  minQty: number;
  qtyUnit: QtyUnit;
  caseCents: Cents; // on-invoice case price once the tier is reached
}

export interface RipTier {
  minQty: number;
  qtyUnit: QtyUnit;
  amountCents: Cents; // single rebate check for the purchase transaction
}

export interface CplDeal {
  frontlineCaseCents: Cents;
  casePack: number;
  discounts: DiscountTier[];
  rip?: { code: string; tiers: RipTier[] } | null;
}

/** Convert a tier minimum to bottles. */
export function tierMinBottles(t: { minQty: number; qtyUnit: QtyUnit }, casePack: number): number {
  return t.qtyUnit === 'case' ? t.minQty * casePack : t.minQty;
}

/** On-invoice case price for buying `bottles` units on one invoice. */
export function invoiceCasePrice(deal: CplDeal, bottles: number): Cents {
  let best = deal.frontlineCaseCents;
  for (const t of deal.discounts) {
    if (bottles >= tierMinBottles(t, deal.casePack) && t.caseCents < best) best = t.caseCents;
  }
  return best;
}

/** Invoice total for `bottles` units (cases and loose bottles priced proportionally, fractions up). */
export function invoiceTotal(deal: CplDeal, bottles: number): Cents {
  const casePrice = invoiceCasePrice(deal, bottles);
  const fullCases = Math.floor(bottles / deal.casePack);
  const loose = bottles % deal.casePack;
  return fullCases * casePrice + Math.ceil((loose * casePrice) / deal.casePack);
}

/**
 * RIP earned on one purchase transaction (one order, one delivery, one invoice). Only the single
 * best tier is paid — paying several RIPs on one transaction is "RIP padding" and not allowed.
 * `bottles` = qualifying units across all member products of the program on that invoice.
 */
export function ripEarned(tiers: RipTier[], bottles: number, casePack: number): { amountCents: Cents; tier: RipTier | null } {
  let best: RipTier | null = null;
  for (const t of tiers) {
    if (bottles >= tierMinBottles(t, casePack) && (!best || t.amountCents > best.amountCents)) best = t;
  }
  return { amountCents: best?.amountCents ?? 0, tier: best };
}

export interface RipLimits {
  maxCases: number;
  smallTierMaxCases: number;
  minBottles: number;
  maxSingleRebateCents: number;
  paymentWindowStartDays: number;
  paymentWindowEndDays: number;
}

/** New Jersey's RIP limits. Other states' packs carry their own. */
export const RIP_LIMITS: Readonly<RipLimits> = {
  maxCases: 50,
  smallTierMaxCases: 5,
  minBottles: 1,
  maxSingleRebateCents: 100_000,
  paymentWindowStartDays: 30,
  paymentWindowEndDays: 90,
};

export interface RipIssue {
  code: 'OVER_50_CASES' | 'NO_SMALL_TIER' | 'OVER_1000' | 'BELOW_MIN' | 'EMPTY';
  message: string;
}

/**
 * Check a RIP program against the default limits. Programs that fail need the Director's prior
 * written approval; the system still loads them but marks them so buyers know.
 */
export function validateRipProgram(tiers: RipTier[], casePack: number, limits: Readonly<RipLimits> = RIP_LIMITS): RipIssue[] {
  const issues: RipIssue[] = [];
  if (tiers.length === 0) return [{ code: 'EMPTY', message: 'RIP program has no tiers' }];
  for (const t of tiers) {
    const bottles = tierMinBottles(t, casePack);
    if (bottles > limits.maxCases * casePack)
      issues.push({ code: 'OVER_50_CASES', message: `Tier at ${t.minQty} ${t.qtyUnit}(s) exceeds 50 cases` });
    if (bottles < limits.minBottles) issues.push({ code: 'BELOW_MIN', message: 'Tier minimum is below 1 bottle' });
    if (t.amountCents > limits.maxSingleRebateCents)
      issues.push({ code: 'OVER_1000', message: `Tier rebate ${(t.amountCents / 100).toFixed(2)} exceeds $1,000` });
  }
  const hasSmall = tiers.some((t) => tierMinBottles(t, casePack) <= limits.smallTierMaxCases * casePack);
  if (!hasSmall) issues.push({ code: 'NO_SMALL_TIER', message: 'No corresponding small-quantity tier of 5 cases or less' });
  return issues;
}

/** RIP check is payable no sooner than 30 and no later than 90 days after the invoice is paid. */
export function ripPaymentWindow(invoicePaidOn: IsoDate, limits: Readonly<RipLimits> = RIP_LIMITS): { start: IsoDate; end: IsoDate } {
  return {
    start: addDays(invoicePaidOn, limits.paymentWindowStartDays),
    end: addDays(invoicePaidOn, limits.paymentWindowEndDays),
  };
}
